import React, { useRef } from 'react'
import { v4 as uuid } from 'uuid'
import tw, { theme, styled } from 'twin.macro'
import EmptyCard from '../image-input/EmptyCard'
import ErrorMessage from '../image-input/ErrorMessage'
import {
  HORIZONTAL_COVER_IMAGE_DISPLAY_WIDTH,
  HORIZONTAL_COVER_IMAGE_DISPLAY_HEIGHT,
} from './ImageConstant'
import { Dispatch } from 'react'
import { SetStateAction } from 'react'
import { useFilesUploader } from './useFilesUploader'
import { UploadTargetType } from '../_shared/image-upload.services'

const DEFAULT_MAX_IMAGE_COUNT = 1

interface FileUploadProps {
  inputId?: string
  onImageUpload: (...args: any[]) => void
  maxImageCount?: number
  onImageUploadStart?: () => void
  onImageUploadEnd?: () => void
  child?: React.ReactNode
  selectedFiles?: Array<any>
  setSelectedFiles?: Dispatch<SetStateAction<any[]>>
  imageType?: string
  isChangeable?: boolean
  width?: number
  height?: number
  target?: UploadTargetType
  hoverEffect?: boolean
}

function FileUpload({
  inputId,
  onImageUpload,
  maxImageCount = DEFAULT_MAX_IMAGE_COUNT,
  onImageUploadStart,
  onImageUploadEnd,
  child,
  selectedFiles,
  setSelectedFiles,
  imageType,
  width = HORIZONTAL_COVER_IMAGE_DISPLAY_WIDTH,
  height = HORIZONTAL_COVER_IMAGE_DISPLAY_HEIGHT,
  target,
}: FileUploadProps) {
  const mask = useRef(null)
  const dropContainer = useRef(null)
  const fileInputRef = useRef<HTMLInputElement>()
  const useFilesUploaderInput = {
    selectedFiles,
    setSelectedFiles,
    imageType,
    maxImageCount,
    onImageAction: onImageUpload,
    target,
  }
  const {
    errorMessages,
    setErrorMessages,
    imageUploadStartCount,
    imageUploadEndCount,
    handleFiles,
  } = useFilesUploader(useFilesUploaderInput)

  const fileDropDragOver = (e) => {
    e.preventDefault()
  }

  const fileDropDragEnter = (e) => {
    e.preventDefault()
    if (e.currentTarget == dropContainer.current) {
      mask.current.style.zIndex = 1
    }
  }

  const fileDropDragLeave = (e) => {
    e.preventDefault()
    if (e.target == dropContainer.current) {
      mask.current.style.zIndex = -1
    }
  }

  /**
   * 因為 `handleFiles` 可能被個別的 ImageCard 呼叫很多次
   * 所以單純只是把 `onImageUploadStart` & `onImageUploadEnd`
   * 放到 `handleFiles` 裡面會出現 race condition
   * 舉例 A:start -> B:start -> A:end -> B:end
   * 會預期 A:start 時要呼叫 `onImageUploadStart`，而 `onImageUploadEnd` 等到 B:end 時再呼叫
   * (B:start 也可以呼叫 `onImageUploadStart`，不影響)
   * 因此用兩個 counter 和 useEffect 來確保正確的 callback 呼叫順序
   */
  React.useEffect(() => {
    if (imageUploadStartCount === 0) return
    onImageUploadStart?.()
  }, [imageUploadStartCount, onImageUploadStart])
  React.useEffect(() => {
    if (imageUploadStartCount === 0) return
    if (imageUploadEndCount === 0) return
    if (imageUploadStartCount !== imageUploadEndCount) return
    onImageUploadEnd?.()
  }, [imageUploadStartCount, imageUploadEndCount, onImageUploadEnd])

  const fileDropDrop = (e) => {
    e.preventDefault()
    const files = e.dataTransfer.files
    if (files.length) {
      handleFiles(files)
    }
    mask.current.style.zIndex = -1
  }

  const fileInputClicked = () => {
    fileInputRef.current.value = null
    fileInputRef.current.click()
  }
  const filesSelected = () => {
    if (fileInputRef.current.files.length) {
      handleFiles(fileInputRef.current.files)
    }
  }

  return (
    <div>
      <DropZone>
        <FileDropContainer
          ref={dropContainer}
          onDragOver={fileDropDragOver}
          onDragEnter={fileDropDragEnter}
          onDragLeave={fileDropDragLeave}
          onDrop={fileDropDrop}
          width={width}
          height={height}
        >
          <svg viewBox={`0 0 ${width} ${height}`} /> {/* aspect-ratio placeholder */}
          {child}
          <EmptyCards onClick={fileInputClicked}>
            {selectedFiles.length === 0 &&
              new Array(maxImageCount - selectedFiles.length).fill('').map((shell, i) => {
                return (
                  <EmptyCard selectedFiles={selectedFiles} maxImageCount={maxImageCount} key={i} />
                )
              })}
            {selectedFiles.length > 0 && selectedFiles.length < maxImageCount && (
              <EmptyCard selectedFiles={selectedFiles} maxImageCount={maxImageCount} />
            )}
          </EmptyCards>
          <input
            id={inputId}
            ref={fileInputRef}
            className="file-input"
            type="file"
            multiple
            onChange={filesSelected}
          />
          <Mask ref={mask} />
        </FileDropContainer>
      </DropZone>
      <div className="error-messages-list">
        {errorMessages
          .filter((value, index, self) => {
            return self.indexOf(value) === index
          })
          .map((error, i) => {
            const id = uuid()
            return (
              <ErrorMessage
                key={id}
                index={i}
                error={error}
                errorMessages={errorMessages}
                setErrorMessages={setErrorMessages}
              />
            )
          })}
      </div>
    </div>
  )
}

export default FileUpload

const DropZone = styled.div`
  background-color: white;
  display: flex;
  overflow-y: hidden;
`

const FileDropContainer = styled.div<{
  width?: number
  height?: number
}>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  .images-list {
    display: flex;
  }
  .file-input {
    display: none;
  }
`
const Mask = styled.div`
  ${tw`absolute`}
  ${tw`bg-white`}
  opacity: .6;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
`
const EmptyCards = styled.div`
  ${tw`flex w-full h-full`}
  ${tw`cursor-pointer`}
`
